import { useEffect, useState } from 'react';

import { useTrackEventAmplitude } from '../../../../core/Tracking/Amplitude/Amplitude';
import { Button } from '../../../../design-system/components/form/button/Button';
import { Typography } from '../../../../design-system/components/typography/Typography';
import { Destination } from '../../../../resources/destination/domain/types/destination';
import { PickDomain } from '../../../../shared/utilities/types/PickDomain';

import style from './AbTripDesignerBanner.module.css';

type Props = {
    destination: PickDomain<Destination, 'id' | 'slug'>;
    source: 'facet' | 'destination' | 'list-itinerary';
};

function IconIA() {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="21"
            height="20"
            viewBox="0 0 21 20"
            fill="none"
        >
            <path
                d="M19.2901 9.09458C15.1959 7.3545 13.2569 5.49228 11.521 1.47437C10.8617 -0.158729 10.3064 -0.705936 9.51492 1.24432C7.78372 5.3995 5.71723 7.38006 1.64847 9.09458C1.23952 9.28298 0.504722 9.61149 0.5 9.99965C0.504722 10.3888 1.23574 10.7163 1.64847 10.9047C5.7125 12.6145 7.78372 14.5998 9.51492 18.755C10.3158 20.7403 10.8731 20.1164 11.521 18.5249C13.2569 14.5089 15.1959 12.6458 19.2901 10.9047C19.7189 10.7002 20.4282 10.4285 20.5 10.0205V9.97409C20.4282 9.56983 19.7246 9.29434 19.2901 9.08984V9.09458Z"
                fill="white"
            />
        </svg>
    );
}

export function AbTripDesignerBanner({ destination, source }: Props) {
    const tripDesignerUrl = `https://trip-designer.evaneos.fr/onboarding/destination?destinationId=${destination.id}&utm_source=evaneos&utm_medium=banner&utm_campaign=page-${source}`;
    const { trackEvent } = useTrackEventAmplitude();
    const [isVisible, setIsVisible] = useState(false);
    const [isNested, setIsNested] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsVisible(true);
            trackEvent((ampli, defaultProperties) => {
                ampli.pathfinderTripDesignerBannerDisplayed({
                    ...defaultProperties,
                    destination_id: destination.id,
                    destination_slug: destination.slug,
                });
            });
        }, 3000); // 3 seconds delay

        return () => clearTimeout(timer);
    }, [destination.id, destination.slug, trackEvent]);

    const handleRedirectToTripBuilder = () => {
        trackEvent((ampli, defaultProperties) => {
            ampli
                .pathfinderTripBuilderBannerClicked({
                    ...defaultProperties,
                    destination_id: destination.id,
                    destination_slug: destination.slug,
                })
                .promise.then(() => {
                    window.location.href = tripDesignerUrl;
                })
                .catch(() => {
                    window.location.href = tripDesignerUrl;
                });
        });
    };

    const handleReduceBanner = () => {
        setIsNested(true);
        trackEvent((ampli, defaultProperties) => {
            ampli.pathfinderTripDesignerBannerClosed({
                ...defaultProperties,
                destination_id: destination.id,
                destination_slug: destination.slug,
            });
        });
    };

    if (!isVisible) {
        return null;
    }

    return (
        <div className={style.container}>
            <div className={style.banner}>
                <div className={style.bannerContent}>
                    {isNested ? (
                        <div
                            className={style.nestedBannerContent}
                            onClick={() => setIsNested(false)}
                        >
                            <Typography scale="primary-body-bold" style={{ flex: 1 }}>
                                Concevez vous-même votre prochain voyage, assisté par l’IA.
                            </Typography>
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 512 512"
                                className={style.bannerActionIcon}
                            >
                                <path
                                    fill="none"
                                    stroke="var(--color-beige-800)"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="48"
                                    d="M112 328l144-144 144 144"
                                />
                            </svg>
                        </div>
                    ) : null}
                    {!isNested ? (
                        <>
                            <div className={style.reduceBanner} onClick={handleReduceBanner}>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    viewBox="0 0 512 512"
                                    className={style.bannerActionIcon}
                                >
                                    <path
                                        fill="none"
                                        stroke="var(--color-beige-800)"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="48"
                                        d="M112 184l144 144 144-144"
                                    />
                                </svg>
                            </div>
                            <div className={style.imageContainer}></div>
                            <div className={style.content}>
                                <div className={style.title}>
                                    <Typography scale="primary-body-bold">
                                        Concevez vous-même votre prochain voyage, assisté par l’IA
                                        avec notre Trip Designer
                                    </Typography>
                                </div>
                                <Button
                                    onClick={handleRedirectToTripBuilder}
                                    priority="secondary-filled"
                                    iconPosition="left"
                                    Icon={IconIA}
                                    className={style.button}
                                >
                                    Commencer
                                </Button>
                            </div>
                        </>
                    ) : null}
                </div>
            </div>
        </div>
    );
}
